import { useEffect, useState } from "react";
import api, { eur, formatApiError } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { GradeRateEditor } from "@/components/GradeRateEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Archive, ArchiveRestore } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useIsManager } from "@/lib/role";

const BLANK = { name: "", legal_name: "", main_contact: "", main_contact_email: "", payment_terms: "30 days", default_charge_rate: 55, vat_treatment: "Standard 23%", status: "active", grade_rates: {} };

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const mgr = useIsManager();
  const [showArch, setShowArch] = useState(false);

  const load = () => api.get("/customers", { params: { include_archived: showArch } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [showArch]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleArchive = async (c) => {
    try { await api.put(`/customers/${c.id}`, { status: c.status === "archived" ? "active" : "archived" }); toast.success(c.status === "archived" ? "Customer restored" : "Customer archived"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openCreate = () => { setEditId(null); setForm(BLANK); setDialog(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name || "", legal_name: c.legal_name || "", main_contact: c.main_contact || "", main_contact_email: c.main_contact_email || "", payment_terms: c.payment_terms || "30 days", default_charge_rate: c.default_charge_rate ?? 55, vat_treatment: c.vat_treatment || "Standard 23%", status: c.status || "active", grade_rates: c.grade_rates || {} });
    setDialog(true);
  };
  const save = async () => {
    const payload = { ...form, default_charge_rate: Number(form.default_charge_rate) };
    try {
      if (editId) { await api.put(`/customers/${editId}`, payload); toast.success("Customer updated"); }
      else { await api.post("/customers", payload); toast.success("Customer created"); }
      setDialog(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout nav={adminNav} title="Customers">
      <PageHeader title="Customers" subtitle={`${rows.length} customers`}>
        <label className="flex items-center gap-2 text-sm text-zinc-600"><Switch data-testid="cust-show-archived" checked={showArch} onCheckedChange={setShowArch} />Show archived</label>
        {!mgr && <Button data-testid="new-customer-btn" onClick={openCreate} className="bg-slate-900 text-white">+ New customer</Button>}
      </PageHeader>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((c) => (
          <div key={c.id} data-testid={`cust-${c.id}`} className={`bg-white border border-zinc-200 rounded-md p-4 min-w-0 ${c.status === "archived" ? "opacity-60" : ""}`}>
            <div className="flex justify-between items-start">
              <div><div className="font-heading font-bold">{c.name}</div><div className="text-xs text-zinc-500">{c.legal_name}</div></div>
              <div className="flex items-center gap-2"><StatusBadge status={c.status} />{!mgr && <button data-testid={`cust-edit-${c.id}`} onClick={() => openEdit(c)} className="text-zinc-400 hover:text-zinc-700"><Pencil className="h-4 w-4" /></button>}{!mgr && <button data-testid={`cust-archive-${c.id}`} title={c.status === "archived" ? "Restore" : "Archive"} onClick={() => toggleArchive(c)} className="text-zinc-400 hover:text-zinc-700">{c.status === "archived" ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</button>}</div>
            </div>
            <div className="mt-3 text-sm space-y-1 text-zinc-600">
              <div>Contact: {c.main_contact || "—"}</div>
              {mgr ? <>
                {c.main_contact_email && <div>{c.main_contact_email}</div>}
                {c.main_contact_phone && <div>{c.main_contact_phone}</div>}
                {c.billing_address && <div className="text-xs text-zinc-400">{c.billing_address}</div>}
              </> : <>
              <div className="num">Default charge: {eur(c.default_charge_rate)}/hr</div>
              <div>Terms: {c.payment_terms} · VAT: {c.vat_treatment}</div>
              {c.grade_rates && Object.keys(c.grade_rates).length > 0 && (
                <div className="text-xs text-zinc-400 pt-1">Type rates: {Object.entries(c.grade_rates).map(([g, r]) => `${g.split(" ")[0]} ${eur(r)}`).join(" · ")}</div>
              )}
              </>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto kqm-scroll">
          <DialogHeader><DialogTitle>{editId ? "Edit customer" : "New customer"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Customer name</Label><Input data-testid="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div className="col-span-2"><Label>Legal name</Label><Input data-testid="c-legal" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Main contact</Label><Input data-testid="c-contact" value={form.main_contact} onChange={(e) => setForm({ ...form, main_contact: e.target.value })} className="mt-1" /></div>
            <div><Label>Contact email</Label><Input data-testid="c-email" value={form.main_contact_email} onChange={(e) => setForm({ ...form, main_contact_email: e.target.value })} className="mt-1" /></div>
            <div><Label>Payment terms</Label><Input data-testid="c-terms" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="mt-1" /></div>
            <div><Label>Default charge €/hr</Label><Input data-testid="c-rate" type="number" value={form.default_charge_rate} onChange={(e) => setForm({ ...form, default_charge_rate: e.target.value })} className="mt-1" /></div>
            <div className="col-span-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="c-status" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2 border-t border-zinc-100 pt-3">
              <GradeRateEditor value={form.grade_rates} onChange={(gr) => setForm({ ...form, grade_rates: gr })} label="Charge-out rates by Type (€/hr) — applies to all this customer's sites unless overridden" />
            </div>
          </div>
          <Button data-testid="c-submit" onClick={save} className="w-full bg-slate-900 text-white mt-2">{editId ? "Save changes" : "Create customer"}</Button>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
